import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { NORFOLK_SOUTHERN_LAYER_KEY } from '@rsos/reduxDataStore/src/features/norfolkSouthern/constants';
import type {
  NorfolkSouthernSpatialObject,
  NorfolkSouthernTrainSelection,
  StateWithSpatialLayers,
} from '@rsos/reduxDataStore/src/features/norfolkSouthern/types';

/**
 * Whether a spatial layer key is the Norfolk Southern train layer. Like the
 * sprite lookup's partial match, a key that contains the layer key (e.g.
 * `norfolk_southern_sandbox`) counts, so the card and the marker agree.
 */
export function isNorfolkSouthernLayerKey(key: unknown): boolean {
  return (
    typeof key === 'string' &&
    key.toLowerCase().includes(NORFOLK_SOUTHERN_LAYER_KEY)
  );
}

function optionalString(value: unknown): string | null {
  if (typeof value === 'number') {
    return String(value);
  }
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function optionalInt(value: unknown): number | null {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed)
    ? Math.round(parsed)
    : null;
}

function coordinatePair(value: unknown): [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) {
    return null;
  }
  const [longitude, latitude] = value;
  if (typeof longitude !== 'number' || typeof latitude !== 'number') {
    return null;
  }
  return [longitude, latitude];
}

/**
 * The train's most recent position. Live objects carry a position history
 * in `allCoordinates` (newest first); objects straight from the cache only
 * have their GeoJSON shape.
 */
export function getSpatialObjectPosition(
  object: NorfolkSouthernSpatialObject,
): { latitude: number; longitude: number } | null {
  const latest = object.allCoordinates?.at(0);
  if (latest && Number.isFinite(latest.lat) && Number.isFinite(latest.lng)) {
    return { latitude: latest.lat, longitude: latest.lng };
  }

  const fromShape = coordinatePair(
    object.shape?.features?.[0]?.geometry?.coordinates,
  );
  const fromCenter = coordinatePair(object.center?.coordinates);
  const pair = fromShape ?? fromCenter;
  return pair ? { longitude: pair[0], latitude: pair[1] } : null;
}

/**
 * Turns a Norfolk Southern spatial object into the selection the consist
 * request form works from, or null when the object is a removal marker or
 * is missing what a request needs (an external ID, a train ID, a position).
 */
const reportedUnusableObjects = new Set<string>();

/**
 * A live object the card and form cannot use is a partner feed problem worth
 * seeing in Sentry, but positions stream every few seconds, so each object
 * is reported once.
 */
function reportUnusableObject(
  object: NorfolkSouthernSpatialObject,
  missing: Record<string, boolean>,
): void {
  const key = `${object.external_id ?? 'unknown'}`;
  if (reportedUnusableObjects.has(key)) {
    return;
  }
  reportedUnusableObjects.add(key);
  const fields = Object.keys(missing)
    .filter(field => missing[field])
    .join(', ');
  // eslint-disable-next-line no-console -- console.error routes to Sentry
  console.error(
    `toTrainSelection: norfolk_southern object ${key} is missing ${fields}`,
  );
}

export function toTrainSelection(
  object: NorfolkSouthernSpatialObject | null | undefined,
): NorfolkSouthernTrainSelection | null {
  if (!object) {
    return null;
  }
  const attributes = object.attributes ?? {};
  if (`${attributes.display ?? ''}`.toLowerCase() === 'false') {
    return null;
  }

  const externalId = optionalString(object.external_id);
  const trainId = optionalString(attributes.train_id);
  const position = getSpatialObjectPosition(object);
  if (!externalId || !trainId || !position) {
    reportUnusableObject(object, {
      external_id: !externalId,
      train_id: !trainId,
      position: !position,
    });
    return null;
  }

  return {
    externalId,
    trainId,
    trainSymbol: optionalString(attributes.train_symbol),
    position,
    speedMph: optionalInt(attributes.speed_mph),
    headingDegrees: optionalInt(attributes.heading_degrees),
    numberOfCars: optionalInt(attributes.number_of_cars),
    trainLengthFeet: optionalInt(attributes.train_length_feet),
    status: optionalString(attributes.status)?.toLowerCase() ?? null,
    originStation: optionalString(attributes.origin_station),
    nextStation: optionalString(attributes.next_station),
    destinationStation: optionalString(attributes.destination_station),
  };
}

const selectDynamicLayers = (state: StateWithSpatialLayers) =>
  state.spatialLayers?.dynamicLayers;

/**
 * Every Norfolk Southern train currently on the map, i.e. the trains the
 * jurisdiction is being fanned positions for. Sorted by train ID so the
 * picker is stable while positions stream in.
 */
export const selectNorfolkSouthernTrains = createSelector(
  [selectDynamicLayers],
  (dynamicLayers): NorfolkSouthernTrainSelection[] => {
    const trains: NorfolkSouthernTrainSelection[] = [];
    for (const layer of dynamicLayers ?? []) {
      if (!isNorfolkSouthernLayerKey(layer.key)) {
        continue;
      }
      for (const object of layer.spatial_objects ?? []) {
        const train = toTrainSelection(object);
        if (train) {
          trains.push(train);
        }
      }
    }
    return trains.sort((a, b) => a.trainId.localeCompare(b.trainId));
  },
);

export function useNorfolkSouthernTrains(): NorfolkSouthernTrainSelection[] {
  return useSelector(selectNorfolkSouthernTrains);
}

/** A train's symbol, or its train ID when it has no symbol. */
export function formatTrainLabel(train: {
  trainId: string;
  trainSymbol?: string | null;
}): string {
  return train.trainSymbol || train.trainId;
}

/** A date as YYYY-MM-DD in local time. */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Whether a string is a real calendar date written as YYYY-MM-DD. */
export function isRealIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return toIsoDate(parsed) === value;
}

/**
 * The origin date at the end of a Norfolk Southern train ID, as YYYY-MM-DD,
 * e.g. `2026-10-07` for `367-2026-10-07`. Empty when the ID does not end in a
 * real date.
 */
export function originDateFromTrainId(trainId: string): string {
  const date = /-(\d{4}-\d{2}-\d{2})$/.exec(trainId.trim())?.[1] ?? '';
  return isRealIsoDate(date) ? date : '';
}
